import { useState, useEffect } from 'react';
import {
  X, ChevronRight, ChevronLeft, Check, UserPlus, Building2,
  MapPin, Link2, Shield, Eye, Settings, RefreshCw, Mail,
  User, Clock, AlertTriangle, Camera, DoorOpen, Radio
} from 'lucide-react';
import { toast } from 'react-toastify';

const STEPS = [
  { id: 'review', label: 'Review', icon: Eye },
  { id: 'organization', label: 'Organization', icon: Building2 },
  { id: 'sites', label: 'Site Access', icon: MapPin },
  { id: 'integrations', label: 'Integrations', icon: Link2 },
  { id: 'confirm', label: 'Confirm', icon: Check }
];

const OnboardingWizard = ({ user, onClose, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState('');
  const [orgRole, setOrgRole] = useState('VIEWER');
  const [allTenants, setAllTenants] = useState([]);
  const [siteAssignments, setSiteAssignments] = useState([]);
  const [siteIntegrations, setSiteIntegrations] = useState({});
  const [vmsUsers, setVmsUsers] = useState({});
  const [feenicsUsers, setFeenicsUsers] = useState({});
  const [mdPermissions, setMdPermissions] = useState({});
  const [integrationSettings, setIntegrationSettings] = useState({});
  const [loadingIntegrations, setLoadingIntegrations] = useState(false);

  useEffect(() => {
    loadOrganizations();
    loadTenants();
  }, []);

  useEffect(() => {
    if (currentStep === 3 && siteAssignments.length > 0) {
      loadIntegrationsForSites();
    }
  }, [currentStep]);

  const loadOrganizations = async () => {
    try {
      const res = await fetch('/api/platform/organizations', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setOrganizations(data || []);
      }
    } catch (err) {
      console.error('Failed to load organizations:', err);
    }
  };

  const loadTenants = async () => {
    try {
      const res = await fetch('/api/platform/tenants', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setAllTenants(data.tenants || data || []);
      }
    } catch (err) {
      console.error('Failed to load tenants:', err);
    }
  };

  const loadIntegrationsForSites = async () => {
    setLoadingIntegrations(true);
    const integrationsMap = {};
    const settingsMap = { ...integrationSettings };

    for (const site of siteAssignments) {
      try {
        const res = await fetch('/api/platform/tenants/' + site.tenantId + '/integrations', { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          integrationsMap[site.tenantId] = (data.integrations || data || []).filter(function(i) { return i.enabled; });
          if (!settingsMap[site.tenantId]) {
            settingsMap[site.tenantId] = {};
          }
          var vmsInt = integrationsMap[site.tenantId].find(function(i) {
            return i.type === 'WAVE_VMS' || i.type === 'MIRASYS_VMS';
          });
          var feenicsInt = integrationsMap[site.tenantId].find(function(i) {
            return i.type === 'FEENICS_ACCESS';
          });
          if (vmsInt && !settingsMap[site.tenantId].vmsIntegrationId) {
            settingsMap[site.tenantId].vmsIntegrationId = vmsInt.id;
            settingsMap[site.tenantId].vmsType = vmsInt.type;
            loadVmsUsers(vmsInt.id);
          }
          if (feenicsInt) {
            settingsMap[site.tenantId].feenicsIntegrationId = feenicsInt.id;
            loadFeenicsUsers(site.tenantId);
          }
        }
      } catch (err) {
        console.error('Failed to load integrations for site ' + site.tenantId + ':', err);
      }
    }
    setSiteIntegrations(integrationsMap);
    setIntegrationSettings(settingsMap);
    setLoadingIntegrations(false);
  };

  const loadVmsUsers = async (integrationId) => {
    try {
      const res = await fetch('/api/vms/integrations/' + integrationId + '/users', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setVmsUsers(function(prev) { return { ...prev, [integrationId]: data.users || [] }; });
      }
    } catch (err) {
      console.error('Failed to load VMS users for integration ' + integrationId + ':', err);
    }
  };
  const loadFeenicsUsers = async (tenantId) => {
    try {
      const res = await fetch("/api/feenics/users?tenantId=" + tenantId, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setFeenicsUsers(function(prev) { return { ...prev, [tenantId]: data.users || [] }; });
      }
    } catch (err) {
      console.error("Failed to load Feenics users for tenant " + tenantId + ":", err);
    }
  };

  const filteredTenants = selectedOrg
    ? allTenants.filter(function(t) { return t.organizationId === selectedOrg; })
    : allTenants;

  const addSite = (tenantId) => {
    const tenant = allTenants.find(function(t) { return t.id === tenantId; });
    if (tenant && !siteAssignments.find(function(s) { return s.tenantId === tenantId; })) {
      setSiteAssignments([...siteAssignments, { tenantId: tenantId, tenantName: tenant.name, role: 'VIEWER' }]);
    }
  };

  const removeSite = (tenantId) => {
    setSiteAssignments(siteAssignments.filter(function(s) { return s.tenantId !== tenantId; }));
    const newSettings = { ...integrationSettings };
    delete newSettings[tenantId];
    setIntegrationSettings(newSettings);
  };

  const updateSiteRole = (tenantId, role) => {
    setSiteAssignments(siteAssignments.map(function(s) {
      return s.tenantId === tenantId ? { ...s, role: role } : s;
    }));
  };

  const updateIntegrationSetting = (tenantId, field, value) => {
    setIntegrationSettings(function(prev) {
      return { ...prev, [tenantId]: { ...prev[tenantId], [field]: value } };
    });
  };

  const updateMdPermission = (tenantId, field, value) => {
    setMdPermissions(function(prev) {
      return { ...prev, [tenantId]: { ...prev[tenantId], [field]: value } };
    });
  };

  const toggleAllMd = (tenantId, enabled) => {
    setMdPermissions(function(prev) {
      return { ...prev, [tenantId]: {
        canViewMD: enabled,
        canManageAgents: enabled,
        canViewEntry: enabled,
        canManageEntryDevices: enabled,
        canManageAlerts: enabled,
        canPromoteDevices: enabled,
        canViewNetworkMap: enabled,
        canAcknowledgeAlerts: enabled
      }};
    });
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0: return true;
      case 1: return !!selectedOrg;
      case 2: return siteAssignments.length > 0;
      case 3: return true;
      case 4: return true;
      default: return false;
    }
  };

  const goNext = () => {
    if (currentStep < STEPS.length - 1 && canProceed()) setCurrentStep(currentStep + 1);
  };

  const goBack = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  const handleApprove = async () => {
    setProcessing(true);
    try {
      const payload = {
        organizationId: selectedOrg,
        orgRole: orgRole,
        siteAssignments: siteAssignments.map(function(s) { return { tenantId: s.tenantId, role: s.role }; }),
        integrationLinks: {}
      };
      for (const site of siteAssignments) {
        const settings = integrationSettings[site.tenantId];
        if (settings) {
          payload.integrationLinks[site.tenantId] = {};
          if (settings.vmsIntegrationId && settings.vmsUserId) {
            payload.integrationLinks[site.tenantId].vms = {
              integrationId: settings.vmsIntegrationId,
              vmsUserId: settings.vmsUserId,
              vmsUsername: settings.vmsUsername || null
            };
          }
          if (settings.feenicsIntegrationId && settings.feenicsUserId) {
            payload.integrationLinks[site.tenantId].feenics = {
              integrationId: settings.feenicsIntegrationId,
              feenicsUserId: settings.feenicsUserId
            };
          }
        }
      }
      payload.mdPermissions = mdPermissions;
      const res = await fetch('/api/platform/pending-registrations/' + user.id + '/approve', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success((user.name || user.email) + ' approved and fully configured!');
        if (onComplete) onComplete();
        onClose();
      } else {
        toast.error(data.error || 'Failed to approve user');
      }
    } catch (err) {
      console.error('Approval error:', err);
      toast.error('Failed to approve user');
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateString) => new Date(dateString).toLocaleString();

  const getOrgName = () => {
    const org = organizations.find(function(o) { return o.id === selectedOrg; });
    return org ? org.name : 'Not selected';
  };

  const renderStepReview = () => (
    <div className="wizard-step-content">
      <div className="wizard-user-card">
        <div className="wizard-user-avatar"><User size={28} /></div>
        <div className="wizard-user-details">
          <h3>{user.name || 'Unnamed User'}</h3>
          <div className="wizard-user-meta">
            <span><Mail size={14} /> {user.email}</span>
            <span><Clock size={14} /> Registered {formatDate(user.createdAt)}</span>
          </div>
        </div>
        <span className="wizard-badge pending">PENDING</span>
      </div>
      <div className="wizard-info-box">
        <AlertTriangle size={16} />
        <div>
          <strong>New Registration Request</strong>
          <p>Review this user and proceed through the setup wizard to configure their access before approving.</p>
        </div>
      </div>
      <div className="wizard-checklist">
        <h4>Setup Checklist</h4>
        <div className="wizard-checklist-item">
          <div className={'wizard-check' + (selectedOrg ? ' done' : '')}>{selectedOrg ? <Check size={12} /> : <span>2</span>}</div>
          <span>Assign to Organization</span>
        </div>
        <div className="wizard-checklist-item">
          <div className={'wizard-check' + (siteAssignments.length > 0 ? ' done' : '')}>{siteAssignments.length > 0 ? <Check size={12} /> : <span>3</span>}</div>
          <span>Configure Site Access</span>
        </div>
        <div className="wizard-checklist-item">
          <div className="wizard-check"><span>4</span></div>
          <span>Link Integrations (optional)</span>
        </div>
      </div>
    </div>
  );

  const renderStepOrganization = () => (
    <div className="wizard-step-content">
      <p className="wizard-step-desc">Assign this user to an organization. This determines which sites they can access.</p>
      <div className="wizard-field">
        <label className="wizard-label">Organization</label>
        <select className="wizard-select" value={selectedOrg} onChange={(e) => { setSelectedOrg(e.target.value); setSiteAssignments([]); setIntegrationSettings({}); }}>
          <option value="">-- Select Organization --</option>
          {organizations.map(function(org) { return <option key={org.id} value={org.id}>{org.name}</option>; })}
        </select>
      </div>
      <div className="wizard-field">
        <label className="wizard-label">Organization Role</label>
        <div className="wizard-role-grid">
          {[
            { value: 'ADMIN', label: 'Admin', desc: 'Full control of organization settings and users', icon: Shield },
            { value: 'OPERATOR', label: 'Operator', desc: 'Can manage day-to-day operations', icon: Settings },
            { value: 'VIEWER', label: 'Viewer', desc: 'View-only access to organization resources', icon: Eye }
          ].map(function(role) {
            return (
              <button key={role.value} type="button" className={'wizard-role-card' + (orgRole === role.value ? ' selected' : '')} onClick={() => setOrgRole(role.value)}>
                <role.icon size={18} />
                <strong>{role.label}</strong>
                <span>{role.desc}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );

  const renderStepSites = () => (
    <div className="wizard-step-content">
      <p className="wizard-step-desc">
        Select which sites this user can access and their role at each site.
        {selectedOrg && <> Showing sites for <strong>{getOrgName()}</strong>.</>}
      </p>
      <div className="wizard-field">
        <label className="wizard-label">Add Site</label>
        <select className="wizard-select" value="" onChange={(e) => { if (e.target.value) addSite(e.target.value); }}>
          <option value="">-- Select a site to add --</option>
          {filteredTenants.filter(function(t) { return !siteAssignments.find(function(s) { return s.tenantId === t.id; }); }).map(function(t) { return <option key={t.id} value={t.id}>{t.name}</option>; })}
        </select>
      </div>
      {siteAssignments.length === 0 ? (
        <div className="wizard-empty-state">
          <MapPin size={32} />
          <p>No sites assigned yet</p>
          <span>Select a site from the dropdown above</span>
        </div>
      ) : (
        <div className="wizard-site-list">
          {siteAssignments.map(function(site) {
            return (
              <div key={site.tenantId} className="wizard-site-row">
                <div className="wizard-site-info">
                  <MapPin size={16} />
                  <strong>{site.tenantName}</strong>
                </div>
                <select className="wizard-select compact" value={site.role} onChange={(e) => updateSiteRole(site.tenantId, e.target.value)}>
                  <option value="VIEWER">Viewer</option>
                  <option value="OPERATOR">Operator</option>
                  <option value="ADMIN">Admin</option>
                </select>
                <button type="button" className="wizard-remove-btn" onClick={() => removeSite(site.tenantId)} title="Remove site"><X size={14} /></button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  const renderStepIntegrations = () => (
    <div className="wizard-step-content">
      <p className="wizard-step-desc">Link this user to VMS and access control systems at each site. This step is optional.</p>
      {loadingIntegrations ? (
        <div className="wizard-loading"><RefreshCw size={20} className="spin" /><span>Loading integrations...</span></div>
      ) : siteAssignments.length === 0 ? (
        <div className="wizard-empty-state"><Link2 size={32} /><p>No sites assigned</p><span>Go back and add sites first</span></div>
      ) : (
        <div className="wizard-integration-list">
          {siteAssignments.map(function(site) {
            const integrations = siteIntegrations[site.tenantId] || [];
            const settings = integrationSettings[site.tenantId] || {};
            const vmsIntegrations = integrations.filter(function(i) { return i.type === 'WAVE_VMS' || i.type === 'MIRASYS_VMS'; });
            const feenicsIntegrations = integrations.filter(function(i) { return i.type === 'FEENICS_ACCESS'; });
            return (
              <div key={site.tenantId} className="wizard-integration-site">
                <div className="wizard-integration-site-header">
                  <MapPin size={14} />
                  <strong>{site.tenantName}</strong>
                  <span className="wizard-badge small">{integrations.length} integrations</span>
                </div>
                {integrations.length === 0 ? (
                  <p className="wizard-integration-none">No enabled integrations at this site</p>
                ) : (
                  <div className="wizard-integration-fields">
                    {vmsIntegrations.length > 0 && (
                      <div className="wizard-integration-block">
                        <label className="wizard-label"><Camera size={14} /> VMS User ({vmsIntegrations[0].type.replace('_', ' ')})</label>
                        {vmsIntegrations.length > 1 && (
                          <select className="wizard-select compact" value={settings.vmsIntegrationId || ''} onChange={(e) => {
                            updateIntegrationSetting(site.tenantId, 'vmsIntegrationId', e.target.value);
                            updateIntegrationSetting(site.tenantId, 'vmsUserId', '');
                            updateIntegrationSetting(site.tenantId, 'vmsUsername', '');
                            if (e.target.value) loadVmsUsers(e.target.value);
                          }}>
                            <option value="">-- Select VMS --</option>
                            {vmsIntegrations.map(function(v) { return <option key={v.id} value={v.id}>{v.type.replace('_', ' ')}</option>; })}
                          </select>
                        )}
                        <select className="wizard-select" value={settings.vmsUserId || ''} onChange={(e) => {
                          const selectedUser = (vmsUsers[settings.vmsIntegrationId] || []).find(function(u) { return u.id === e.target.value; });
                          updateIntegrationSetting(site.tenantId, 'vmsUserId', e.target.value);
                          updateIntegrationSetting(site.tenantId, 'vmsUsername', selectedUser ? (selectedUser.displayName || selectedUser.username || selectedUser.name || '') : '');
                        }}>
                          <option value="">-- Select VMS User (optional) --</option>
                          {(vmsUsers[settings.vmsIntegrationId] || []).map(function(u) { return <option key={u.id} value={u.id}>{u.displayName || u.username || u.name}{u.email ? ' (' + u.email + ')' : ''}</option>; })}
                        </select>
                      </div>
                    )}
                    {feenicsIntegrations.length > 0 && (
                      <div className="wizard-integration-block">
                        <label className="wizard-label"><DoorOpen size={14} /> Feenics User (Access Control)</label>
                        <select className="wizard-select" value={settings.feenicsUserId || ''} onChange={(e) => {
                          var sel = (feenicsUsers[site.tenantId] || []).find(function(u) { return u.id === e.target.value; });
                          updateIntegrationSetting(site.tenantId, 'feenicsUserId', e.target.value);
                          updateIntegrationSetting(site.tenantId, 'feenicsUsername', sel ? (sel.name || sel.username) : '');
                        }}>
                          <option value="">-- Select Feenics User (optional) --</option>
                          {(feenicsUsers[site.tenantId] || []).map(function(u) { return <option key={u.id} value={u.id}>{u.name || u.username}{u.email ? ' (' + u.email + ')' : ''}</option>; })}
                        </select>
                      </div>
                    )}
                    <div className="wizard-integration-block" style={{marginTop: '12px', paddingTop: '12px', borderTop: '1px solid var(--border-light, #1e2d3d)'}}>
                      <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                        <label className="wizard-label" style={{marginBottom: 0}}><Radio size={14} /> CCM Monitor Access</label>
                        <label style={{display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--text-muted)', cursor: 'pointer'}}>
                          <input type="checkbox" checked={!!(mdPermissions[site.tenantId] || {}).canViewMD} onChange={(e) => toggleAllMd(site.tenantId, e.target.checked)} />
                          Enable All
                        </label>
                      </div>
                      {(mdPermissions[site.tenantId] || {}).canViewMD && (
                        <div className="wizard-md-toggles" style={{display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '6px', marginTop: '8px'}}>
                          {[
                            {key: 'canManageAgents', label: 'Manage Agents'},
                            {key: 'canManageAlerts', label: 'Manage Alerts'},
                            {key: 'canPromoteDevices', label: 'Promote Devices'},
                            {key: 'canViewNetworkMap', label: 'View Network Map'},
                            {key: 'canAcknowledgeAlerts', label: 'Acknowledge Alerts'}
                          ].map(function(perm) {
                            return (
                              <label key={perm.key} style={{display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--text-secondary)', cursor: 'pointer', padding: '4px 0'}}>
                                <input type="checkbox" checked={!!(mdPermissions[site.tenantId] || {})[perm.key]} onChange={(e) => updateMdPermission(site.tenantId, perm.key, e.target.checked)} />
                                {perm.label}
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  const renderStepConfirm = () => (
    <div className="wizard-step-content">
      <p className="wizard-step-desc">Review the configuration below and confirm to approve <strong>{user.name || user.email}</strong>.</p>
      <div className="wizard-summary">
        <div className="wizard-summary-section">
          <h4><User size={16} /> User</h4>
          <div className="wizard-summary-row"><span>Name</span><strong>{user.name || 'Unnamed'}</strong></div>
          <div className="wizard-summary-row"><span>Email</span><strong>{user.email}</strong></div>
        </div>
        <div className="wizard-summary-section">
          <h4><Building2 size={16} /> Organization</h4>
          <div className="wizard-summary-row"><span>Organization</span><strong>{getOrgName()}</strong></div>
          <div className="wizard-summary-row"><span>Org Role</span><span className={'wizard-badge role ' + orgRole.toLowerCase()}>{orgRole}</span></div>
        </div>
        <div className="wizard-summary-section">
          <h4><MapPin size={16} /> Site Access ({siteAssignments.length})</h4>
          {siteAssignments.map(function(site) {
            return <div key={site.tenantId} className="wizard-summary-row"><span>{site.tenantName}</span><span className={'wizard-badge role ' + site.role.toLowerCase()}>{site.role}</span></div>;
          })}
        </div>
        <div className="wizard-summary-section">
          <h4><Link2 size={16} /> Integration Links</h4>
          {siteAssignments.map(function(site) {
            const settings = integrationSettings[site.tenantId] || {};
            const hasVms = settings.vmsUserId;
            const hasFeenics = settings.feenicsUserId;
            const siteMd = mdPermissions[site.tenantId] || {};
            const hasMd = siteMd.canViewMD;
            if (!hasVms && !hasFeenics && !hasMd) {
              return <div key={site.tenantId} className="wizard-summary-row muted"><span>{site.tenantName}</span><span>No integrations linked</span></div>;
            }
            return (
              <div key={site.tenantId} className="wizard-summary-sub">
                <span className="wizard-summary-sub-title">{site.tenantName}</span>
                {hasVms && <div className="wizard-summary-row"><span><Camera size={12} /> VMS</span><strong>{settings.vmsUsername || settings.vmsUserId}</strong></div>}
                {hasFeenics && <div className="wizard-summary-row"><span><DoorOpen size={12} /> Feenics</span><strong>{settings.feenicsUsername || settings.feenicsUserId}</strong></div>}
                {hasMd && <div className="wizard-summary-row"><span><Radio size={12} /> CCM Monitor</span><strong>{[siteMd.canManageAgents && 'Agents', siteMd.canManageAlerts && 'Alerts', siteMd.canPromoteDevices && 'Promote', siteMd.canViewNetworkMap && 'Network', siteMd.canAcknowledgeAlerts && 'Ack Alerts'].filter(Boolean).join(', ') || 'View Only'}</strong></div>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 0: return renderStepReview();
      case 1: return renderStepOrganization();
      case 2: return renderStepSites();
      case 3: return renderStepIntegrations();
      case 4: return renderStepConfirm();
      default: return null;
    }
  };

  return (
    <div className="wizard-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="wizard-modal">
        <div className="wizard-header">
          <div className="wizard-header-left"><UserPlus size={20} /><h2>User Onboarding</h2></div>
          <button className="wizard-close" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="wizard-stepper">
          {STEPS.map(function(step, idx) {
            return (
              <div key={step.id} className={'wizard-stepper-item' + (idx === currentStep ? ' active' : '') + (idx < currentStep ? ' completed' : '')} onClick={() => { if (idx < currentStep) setCurrentStep(idx); }}>
                <div className="wizard-stepper-dot">{idx < currentStep ? <Check size={12} /> : <step.icon size={12} />}</div>
                <span className="wizard-stepper-label">{step.label}</span>
              </div>
            );
          })}
        </div>
        <div className="wizard-body">{renderCurrentStep()}</div>
        <div className="wizard-footer">
          <div className="wizard-footer-left">
            {currentStep > 0 && <button className="wizard-btn secondary" onClick={goBack}><ChevronLeft size={16} /> Back</button>}
          </div>
          <div className="wizard-footer-right">
            {currentStep < STEPS.length - 1 ? (
              <button className="wizard-btn primary" onClick={goNext} disabled={!canProceed()}>Next <ChevronRight size={16} /></button>
            ) : (
              <button className="wizard-btn approve" onClick={handleApprove} disabled={processing}>
                {processing ? <><RefreshCw size={16} className="spin" /> Approving...</> : <><Check size={16} /> Approve & Activate</>}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingWizard;
